// -- External libraries
import {
    createSignal,
    createUniqueId,
    mergeProps,
    onCleanup,
    onMount,
} from 'solid-js'

/*
 * Everything Tab can land on inside the card. `:not([disabled])` and the
 * negative-tabindex exclusion are what separate "focusable" from "in the tab
 * order"; `offsetParent` then drops anything currently hidden, so a trap never
 * parks focus on an invisible control.
 */
const TABBABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]',
].map((selector) => `${selector}:not([tabindex="-1"])`).join(', ')

const tabbableElementsWithin = (root) => [...root.querySelectorAll(TABBABLE_SELECTOR)]
    .filter((element) => element.offsetParent !== null)

/*
 * Where Tab should send focus inside a trapping modal, as a verdict rather than
 * a DOM call, so the wrap-around rules can be tested without a browser.
 *
 * `activeIndex` is the focused element's position in the card's tabbable list,
 * or -1 when focus is on the card itself. `undefined` means "no intervention" —
 * the browser's own Tab order already keeps focus inside the card.
 *
 * TENANT-5581: `focusInsideCard: false` is the case that made the gate
 * bypassable. Focus can leave the card without a Tab we ever saw — a trip
 * through the browser chrome and back drops it into the page underneath — and
 * returning early there let the next Tab walk the page behind an open modal.
 * A trapping modal claims the next Tab wherever focus currently sits.
 */
export const nextTrapFocusTarget = ({
    activeIndex,
    focusInsideCard,
    goingBackwards,
    tabbableCount,
}) => {
    // Nothing to land on: park focus on the card rather than let Tab walk out.
    if (tabbableCount === 0) return 'card'

    if (!focusInsideCard) return goingBackwards ? 'last' : 'first'

    if (goingBackwards && (activeIndex === 0 || activeIndex === -1)) return 'last'
    if (!goingBackwards && activeIndex === tabbableCount - 1) return 'first'

    // Falls through with no verdict: the browser's own Tab order already keeps
    // focus inside the card.
}

/*
 * Whether a modal's card is the topmost one and should answer Escape, as a verdict over the
 * cards found in the document so the rule can be tested without a browser (TENANT-5638, QA
 * bug 14: Esc did nothing on the account Tax information modal while ✕ worked — the only gate
 * between the two is this check).
 *
 * `cards` is every modal card in document order, `own` this modal's card. A card that is not
 * displayed (a wrapper that keeps a closed modal mounted, a card mid-transition) is not on top
 * of anything, so only displayed cards count. A modal that cannot find its own card among them
 * is not stacked under anything it can see either: it answers, as every modal did before the
 * stacking rule (TENANT-5589) — the rule exists to stop two stacked modals closing on one press,
 * never to make a lone modal ignore the key.
 */
export const isTopmostCard = ({
    cards,
    own,
    isDisplayed = () => true,
}) => {
    const displayed = cards.filter((card) => isDisplayed(card))
    if (displayed.length === 0 || !displayed.includes(own)) return true
    return displayed.at(-1) === own
}

export function useModalOrganismComponentController (props) {
    const defaultProps = {}
    const mergedProps = mergeProps(defaultProps, props)

    const [
        modalReference,
        setModalReference,
    ] = createSignal(HTMLDivElement | undefined)

    /*
     * Names the dialog for a screen reader. Generated rather than taken as a
     * prop so every caller that passes a `title` gets an accessible name without
     * inventing an id — and pointed at only when there IS a title, since a
     * `header` slot leaves the title element empty and an `aria-labelledby` at
     * an empty element is worse than none.
     */
    const titleId = createUniqueId()

    /*
     * Every open modal listens for Escape on the document, so with two stacked
     * (a price breakdown opening a tax breakdown, say) one press closed both and
     * the focus the inner one wanted to hand back had nowhere to go
     * (TENANT-5589). Portals append in open order, so the last card in the
     * document is the topmost: only it answers Escape; the one under it gets
     * the next press.
     */
    const isTopmostModal = () => isTopmostCard({
        cards: [...document.querySelectorAll('.modal-organism-component__card')],
        // offsetParent is null for display:none (and for a detached node): not on top of anything.
        isDisplayed: (card) => card.offsetParent !== null,
        own: modalReference(),
    })

    const handleEscKey = (event) => {
        // Respect noClose so non-dismissable modals (e.g. the legal acceptance
        // gate) can't be escaped with the keyboard either.
        if (event.key === 'Escape' && !mergedProps.noClose && isTopmostModal()) {
            mergedProps.onClose()
        }
    }

    /*
     * Focus trap, OPT-IN via `trapFocus`. It is not on by default only because
     * this component has 70-odd call sites that have never been audited for it
     * (a modal embedding a third-party iframe, say, needs its own thinking);
     * a modal that asks for it gets the standard wrap-around behaviour.
     */
    const handleTabKey = (event) => {
        if (event.key !== 'Tab' || !mergedProps.trapFocus) return

        const card = modalReference()
        if (!card) return

        const tabbable = tabbableElementsWithin(card)
        const target = nextTrapFocusTarget({
            activeIndex: tabbable.indexOf(document.activeElement),
            focusInsideCard: card.contains(document.activeElement),
            goingBackwards: event.shiftKey,
            tabbableCount: tabbable.length,
        })
        if (!target) return

        event.preventDefault()
        if (target === 'card') card.focus()
        else if (target === 'first') tabbable[0].focus()
        else tabbable.at(-1).focus()
    }

    const handleKeyDown = (event) => {
        handleEscKey(event)
        handleTabKey(event)
    }

    /*
     * Where focus goes when the modal closes, OPT-IN via `restoreFocus`. Captured
     * before the modal steals focus and re-checked on the way out — the trigger
     * can legitimately be gone by then (a row the modal deleted), and focusing a
     * detached node silently sends focus to the body.
     */
    let elementFocusedBeforeOpen

    onMount(() => {
        elementFocusedBeforeOpen = document.activeElement

        // Disable scrolling when the modal opens
        document.body.style.overflow = 'hidden'

        modalReference().focus()
        document.addEventListener('keydown', handleKeyDown)
    })

    onCleanup(() => {
        document.body.style.overflow = '' // Restore scrolling
        document.removeEventListener('keydown', handleKeyDown)

        if (
            mergedProps.restoreFocus
            && elementFocusedBeforeOpen
            && elementFocusedBeforeOpen.isConnected
            && typeof elementFocusedBeforeOpen.focus === 'function'
        ) {
            elementFocusedBeforeOpen.focus()
        }
    })

    return {
        mergedProps,
        setModalReference,
        titleId,
    }
}
